import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Warehouse, Cpu, TrendingUp } from "lucide-react";
import { Reveal, Eyebrow, PageHeader, DirectorAvatar, DirectorProfileDialog } from "../components/common";
import { FIRMS, DISTRICTS, AWARD_CATEGORIES, DIRECTORS } from "../mock";

const CAT_ICONS = [Warehouse, Cpu, TrendingUp];

export default function About() {
  return (
    <>
      <PageHeader
        crumb="About"
        title="The authorised link between OEMs and the retail counter"
        intro="Rajiv Syndicate Group is not a manufacturer — it is the authorised distributor that keeps genuine Maruti Suzuki, Amaron and Hero MotoCorp parts moving into retail outlets across Northern Haryana."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-2 gap-14">
        <Reveal>
          <Eyebrow>Who we are</Eyebrow>
          <h2 className="font-head font-extrabold text-3xl md:text-4xl leading-tight mt-4">Practical, logistics-first, and territorial by design</h2>
        </Reveal>
        <Reveal delay={120} className="self-center">
          <p className="text-[var(--rs-muted)] text-lg leading-relaxed">
            The Group's credibility is stated through OEM authorisations and operational footprint, not lifestyle claims — three brands, seven districts, and a warehouse discipline built up since 1996. Modern inventory management techniques and regular staff training keep the promise simple: the right part reaches the right place at the right time.
          </p>
        </Reveal>
      </section>

      {/* Group structure */}
      <section className="bg-[var(--rs-bg)] border-y border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><Eyebrow>Group structure</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Four firms, one leadership</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {FIRMS.map((f, i) => (
              <Reveal key={f} delay={i * 90}>
                <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-7 h-full">
                  <span className="font-mono-label text-sm text-[var(--rs-blue)]">0{i + 1}</span>
                  <h3 className="font-head font-semibold text-lg mt-4">{f}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Footprint */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal><Eyebrow>Geographic footprint</Eyebrow></Reveal>
        <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4 max-w-3xl">Based in Ambala Cantt, operating across seven districts</h2></Reveal>
        <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-3xl">Kuldeep Nagar, GT Road, Ambala Cantt is home base — operations reach Ambala, Kurukshetra, Karnal, Yamuna Nagar, Kaithal, Panipat and Sonipat, essentially the entire northern Haryana belt.</p></Reveal>
        <div className="flex flex-wrap gap-3 mt-10">
          {DISTRICTS.map((d, i) => (
            <Reveal key={d} delay={i * 40}><span className="chip inline-block bg-white border border-[var(--rs-border)] rounded-lg px-5 py-3 text-sm font-medium cursor-default">{d}</span></Reveal>
          ))}
        </div>
        <Reveal delay={120}><Link to="/locations" className="btn-outline rounded-lg px-6 py-3 text-sm font-semibold inline-block mt-10">View All 15 Outlets</Link></Reveal>
      </section>

      {/* Credibility */}
      <section className="bg-[var(--rs-bg)] border-y border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><Eyebrow>Credibility markers</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Fifteen years of recognition</h2></Reveal>
          <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-2xl">Awarded across three categories that matter most to an authorised distributor: warehouse operations, technology use, and sales growth.</p></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {AWARD_CATEGORIES.map((c, i) => {
              const Icon = CAT_ICONS[i];
              return (
                <Reveal key={c.title} delay={i * 110}>
                  <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-7 h-full">
                    <div className="w-12 h-12 rounded-xl bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center"><Icon size={22} /></div>
                    <h3 className="font-head font-bold text-xl mt-5">{c.title}</h3>
                    <p className="text-[var(--rs-muted)] text-sm mt-3 leading-relaxed">{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal><Eyebrow>Leadership</Eyebrow></Reveal>
        <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Directed by Sanjay Jain and Rajiv Jain</h2></Reveal>
        <div className="grid sm:grid-cols-2 gap-6 mt-12 max-w-3xl">
          {DIRECTORS.map((d, i) => (
            <Reveal key={d.name} delay={i * 120}>
              <DirectorProfileDialog director={d}>
                <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-7 flex items-center gap-5">
                  <DirectorAvatar director={d} size={80} />
                  <div>
                    <h3 className="font-head font-bold text-xl">{d.name}</h3>
                    <p className="text-[var(--rs-muted)] text-sm mt-1">{d.role}</p>
                  </div>
                </div>
              </DirectorProfileDialog>
            </Reveal>
          ))}
        </div>
        <Reveal delay={120}>
          <div className="flex flex-wrap gap-4 mt-12">
            <Link to="/history" className="btn-primary rounded-lg px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">Read Our History <ArrowRight size={16} /></Link>
            <Link to="/contact" className="btn-outline rounded-lg px-6 py-3 text-sm font-semibold inline-flex items-center gap-2">Contact Us <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
